import { useEffect, useRef } from 'react';

export default function useInfiniteScroll({
  dataMap,
  setData,
  setStartIndexForTable,
  startIndexForTable,
  pageSize,
  sortByCol, 
  setSortByCol,
  sortByAsc, 
  setSortByAsc,
  tableWrapperRef,
  searchValue,
  tableRef,
  rowHeight = 70,
}) {
  // Disable Scroll
  const disableScroll = (el) => {
    let scrollTop = el.scrollTop;
    let scrollLeft = el.scrollLeft;

    el.onscroll = function() {
      el.scrollTo(scrollLeft, scrollTop);
    };
  };

  // Enable Scroll
  const enableScroll = (el) => {
    el.onscroll = function() {};
  };

  // Generate Data Slice
  const generateDataSlice = (map, startIndex = 0, length = pageSize) => {
    if (!map) return [];
    const viewableIds = map.sortedIds.slice(startIndex, startIndex + length);
    return viewableIds.map((key) => ({ id: key, ...map.data[key] }));
  };

  // Sort Map
  const sortMap = (map, filteredIds) => {
    // Apply search filter
    let ids = filteredIds || map.filteredIds;

    // Get only the root level IDs (those in map.ids)
    const rootIds = ids.filter(id => map.ids.includes(id));
  
    // Sort the root level IDs
    const sortedRootIds = [...rootIds].sort((a, b) => {
      const valueA = map.data[a][sortByCol];
      const valueB = map.data[b][sortByCol];
  
      if (valueA === undefined || valueA === null) {
        return sortByAsc ? 1 : -1;
      }
  
      if (valueB === undefined || valueB === null) {
        return sortByAsc ? -1 : 1;
      }
  
      // Check if valueA and valueB are both strings
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        const dateA = new Date(valueA);
        const dateB = new Date(valueB);
        if (!isNaN(dateA) && !isNaN(dateB)) {
          // Handle date strings
          if (sortByAsc) {
            return dateA - dateB;
          } else {
            return dateB - dateA;
          }
        } else {
          // Handle non-date strings
          if (sortByAsc) {
            return valueA.localeCompare(valueB);
          } else {
            return valueB.localeCompare(valueA);
          }
        }
      } else if (valueA instanceof Date && valueB instanceof Date) {
        // Handle date values
        if (sortByAsc) {
          return valueA - valueB;
        } else {
          return valueB - valueA;
        }
      } else {
        // Handle non-string, non-date values by comparing them directly
        if (sortByAsc) {
          return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
        } else {
          return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
        }
      }
    });

    // Recursively get all visible children for a site
    const getVisibleChildren = (parentId, allIds) => {
      const children = allIds.filter(id => {
        const item = map.data[id];
        return item.parentId === parentId;
      });

      const visibleChildren = [];
      children.forEach(childId => {
        visibleChildren.push(childId);
        // If this child is expanded, recursively get its children
        if (map.data[childId].isExpanded) {
          const grandChildren = getVisibleChildren(childId, allIds);
          visibleChildren.push(...grandChildren);
        }
      });

      return visibleChildren;
    };

    // Create the final sorted array with all levels of children in their original positions
    const finalSortedIds = [];
    sortedRootIds.forEach(rootId => {
      finalSortedIds.push(rootId);
      // If this root item is expanded, add all its visible descendants
      if (map.data[rootId].isExpanded) {
        const visibleChildren = getVisibleChildren(rootId, ids);
        finalSortedIds.push(...visibleChildren);
      }
    });

    map.sortedIds = finalSortedIds;
    return map.sortedIds;
  }  

  // On Re-Render Interval
  const onReRenderInterval = () => {
    const el = tableWrapperRef.current;
    const ROW_HEIGHT = rowHeight;

    if (!el) return;

    const scrollTop = el.scrollTop;
    const newStartIndex = Math.floor(scrollTop / ROW_HEIGHT) - ROW_HEIGHT;
    const topStop = Math.max((startIndexForTable + 10) * ROW_HEIGHT);
    const botStop = Math.max((startIndexForTable + pageSize - 30) * ROW_HEIGHT);

    if (newStartIndex <= 0 && startIndexForTable <= 0) {
      return;
    }

    if (scrollTop < topStop || scrollTop > botStop) {
      const newIndex = Math.max(newStartIndex, 0);
      // console.log('newIndex', newIndex);
      setStartIndexForTable(newIndex);
      setData(generateDataSlice(dataMap, newIndex));
      disableScroll(el);
      setTimeout(() => enableScroll(el), 100);
    }
  };

  /**
   * Set which column to sort by
   * @param {*} colId - the id of the column to sort by
   * @returns 
   */
  const setSortBy = (colId) => {
    // console.log("setting sort by", colId)
    setStartIndexForTable(0);
    if(sortByCol === colId){
      return setSortByAsc(!sortByAsc)
    }
    setSortByCol(colId);
  }

  // Effect to reset the table scroll position when the startIndexForTable changes
  useEffect(() => {
    if (startIndexForTable === 0) {
      tableWrapperRef.current?.scrollTo(0, 0);
    }
  }, [startIndexForTable]);

  // Effect to initialize and clean up
  useEffect(() => {
    const intervalId = window.setInterval(onReRenderInterval, 500);
    return () => window.clearInterval(intervalId);
  }, [startIndexForTable, pageSize, sortByCol, sortByAsc, dataMap]);

  // Trigger search and sort when sortByCol or sortByAsc changes
  useEffect(()=>{ 
    if(searchValue !== ""){
      tableRef.current.search(dataMap, startIndexForTable)
    } else {
      sortMap(dataMap);
      setData(generateDataSlice(dataMap, startIndexForTable, 100))
    }
  }, [sortByCol, sortByAsc])

  // Return all functions for use in the parent component
  return {
    sortMap,
    generateDataSlice,
    disableScroll,
    enableScroll,
    onReRenderInterval,
    setSortBy,
  };
};
